// Dialogs for chat: new chat, teams (create, browse, manage) and adding people.
import { useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { keys, type Conversation, type Presence, type Team, type TeamSummary } from "../../lib/chat";
import { Alert, Avatar, Badge, Button, Dialog, Field, Select, Spinner, cx } from "../../components/ui";
import { Icon } from "../../components/icons";
import { atLeast, errorText, useOrg } from "../org";
import { isDnd, useNow, usePerson } from "../../lib/people";

type Member = { id: string; name: string; email: string; role: string; status: string };

// Someone's presence; given their user id, Do not disturb (while they're around) shows instead.
export function PresenceDot({ status, userId, className }: { status: Presence | undefined; userId?: string; className?: string }) {
  const person = usePerson(userId);
  const now = useNow();
  const raw = status ?? "offline";
  const s = raw !== "offline" && isDnd(person?.dndUntil, now) ? "dnd" : raw;
  const label = s === "online" ? "Online" : s === "away" ? "Away" : s === "busy" ? "In a call" : s === "dnd" ? "Do not disturb" : "Offline";
  return (
    <span
      className={cx("inline-grid h-2.5 w-2.5 place-items-center rounded-full ring-2 ring-white dark:ring-slate-900", s === "online" ? "bg-emerald-500" : s === "away" ? "bg-amber-400" : s === "busy" || s === "dnd" ? "bg-red-500" : "bg-slate-300 dark:bg-slate-600", className)}
      role="img" aria-label={label} title={s === "busy" || s === "dnd" ? label : undefined}
    >
      {s === "dnd" && <span className="h-[2px] w-1.5 rounded bg-white" />}
    </span>
  );
}

export function usePresence() {
  const { org } = useOrg();
  return useQuery({ queryKey: keys.presence(org.slug), queryFn: () => api<{ presence: Record<string, Presence> }>(`/orgs/${org.slug}/presence`), staleTime: 60_000 }).data?.presence ?? {};
}

// Choose people from the organization (members and above can list it).
export function PeoplePicker({ selected, onChange, exclude = [], limitTo }: { selected: Member[]; onChange: (people: Member[]) => void; exclude?: string[]; limitTo?: string[] }) {
  const { org, role, me } = useOrg();
  const [q, setQ] = useState("");
  const presence = usePresence();
  const members = useQuery({ queryKey: keys.members(org.slug), queryFn: () => api<{ members: Member[] }>(`/orgs/${org.slug}/members`), enabled: atLeast(role, "member") });
  const options = useMemo(() => {
    const hidden = new Set([me.user.id, ...exclude, ...selected.map((s) => s.id)]);
    const allowed = limitTo ? new Set(limitTo) : null;
    const needle = q.trim().toLowerCase();
    return (members.data?.members ?? [])
      .filter((m) => m.status === "active" && !hidden.has(m.id) && (!allowed || allowed.has(m.id)))
      .filter((m) => !needle || m.name.toLowerCase().includes(needle) || m.email.toLowerCase().includes(needle))
      .slice(0, 50);
  }, [members.data, q, selected, exclude, limitTo, me.user.id]);

  if (!atLeast(role, "member")) return <Alert kind="info">Guests can't browse the organization's people. Ask a member to start the conversation.</Alert>;
  return (
    <div>
      {selected.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {selected.map((p) => (
            <span key={p.id} className="inline-flex items-center gap-1 rounded-full bg-brand-50 py-1 pl-2.5 pr-1 text-sm text-brand-700 dark:bg-brand-700/30 dark:text-brand-100">
              {p.name}
              <button type="button" onClick={() => onChange(selected.filter((s) => s.id !== p.id))} aria-label={`Remove ${p.name}`} className="rounded-full p-0.5 hover:bg-brand-100 dark:hover:bg-brand-700/50"><Icon name="x" className="h-3.5 w-3.5" /></button>
            </span>
          ))}
        </div>
      )}
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" aria-label="Search people" autoFocus
        className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
      <ul className="mt-2 max-h-64 overflow-y-auto rounded-lg ring-1 ring-slate-200 dark:ring-slate-800">
        {members.isLoading && <li className="flex justify-center p-4 text-brand-600"><Spinner /></li>}
        {!members.isLoading && options.length === 0 && <li className="p-4 text-center text-sm text-slate-500">No one to add.</li>}
        {options.map((m) => (
          <li key={m.id}>
            <button type="button" onClick={() => { onChange([...selected, m]); setQ(""); }} className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800">
              <span className="relative"><Avatar name={m.name} userId={m.id} size="sm" /><PresenceDot status={presence[m.id]} userId={m.id} className="absolute -bottom-0.5 -right-0.5" /></span>
              <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium">{m.name}</span><span className="block truncate text-xs text-slate-500">{m.email}</span></span>
              {m.role === "guest" && <Badge tone="amber">Guest</Badge>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function NewChatDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { org } = useOrg();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [people, setPeople] = useState<Member[]>([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const close = () => { setPeople([]); setName(""); setError(null); onClose(); };
  async function start(e: FormEvent) {
    e.preventDefault();
    if (!people.length) return;
    setBusy(true); setError(null);
    try {
      const r = people.length === 1
        ? await api<{ conversation: Conversation }>(`/orgs/${org.slug}/dms`, { method: "POST", body: { userId: people[0]!.id } })
        : await api<{ conversation: Conversation }>(`/orgs/${org.slug}/groups`, { method: "POST", body: { userIds: people.map((p) => p.id), name: name.trim() || undefined } });
      await qc.invalidateQueries({ queryKey: keys.conversations(org.slug) });
      close();
      navigate(`/o/${org.slug}/chat/${r.conversation.id}`);
    } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onClose={close} title="New chat">
      <form onSubmit={start} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        <PeoplePicker selected={people} onChange={setPeople} />
        {people.length >= 2 && <Field label="Group name (optional)" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="e.g. Launch planning" />}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={close}>Cancel</Button>
          <Button type="submit" loading={busy} disabled={!people.length}>{people.length >= 2 ? "Start group chat" : "Start chat"}</Button>
        </div>
      </form>
    </Dialog>
  );
}

export function CreateTeamDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { org } = useOrg();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  async function create(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    setBusy(true); setError(null); setFields({});
    try {
      const r = await api<{ team: Team }>(`/orgs/${org.slug}/teams`, { method: "POST", body: { name: data.get("name"), description: data.get("description") || undefined, visibility: data.get("visibility") } });
      await Promise.all([qc.invalidateQueries({ queryKey: keys.teams(org.slug) }), qc.invalidateQueries({ queryKey: keys.conversations(org.slug) })]);
      onClose();
      const general = r.team.channels.find((c) => c.isGeneral);
      if (general) navigate(`/o/${org.slug}/chat/${general.id}`);
    } catch (err) {
      setError(errorText(err));
      if (err && typeof err === "object" && "fields" in err) setFields((err as { fields: Record<string, string> }).fields);
    } finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onClose={onClose} title="Create a team">
      <form onSubmit={create} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        <Field label="Team name" name="name" required minLength={2} maxLength={60} placeholder="e.g. Marketing" error={fields.name} />
        <Field label="Description (optional)" name="description" maxLength={300} placeholder="What's this team for?" />
        <Select label="Who can find and join it" name="visibility" defaultValue="public">
          <option value="public">Public: anyone in {org.name} can join</option>
          <option value="private">Private: only people the owners add</option>
        </Select>
        <div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={onClose}>Cancel</Button><Button type="submit" loading={busy}>Create team</Button></div>
      </form>
    </Dialog>
  );
}

export function BrowseTeamsDialog({ open, onClose, onCreate, onManage }: { open: boolean; onClose: () => void; onCreate: () => void; onManage: (teamId: string) => void }) {
  const { org, role } = useOrg();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const teams = useQuery({ queryKey: keys.teams(org.slug), queryFn: () => api<{ teams: TeamSummary[] }>(`/orgs/${org.slug}/teams`), enabled: open });
  async function join(t: TeamSummary) {
    setBusy(t.id); setError(null);
    try {
      const r = await api<{ team: Team }>(`/orgs/${org.slug}/teams/${t.id}/join`, { method: "POST" });
      await Promise.all([qc.invalidateQueries({ queryKey: keys.teams(org.slug) }), qc.invalidateQueries({ queryKey: keys.conversations(org.slug) })]);
      onClose();
      const general = r.team.channels.find((c) => c.isGeneral);
      if (general) navigate(`/o/${org.slug}/chat/${general.id}`);
    } catch (err) { setError(errorText(err)); } finally { setBusy(null); }
  }
  return (
    <Dialog open={open} onClose={onClose} title="Teams">
      <div className="space-y-4">
        {error && <Alert>{error}</Alert>}
        <ul className="max-h-96 divide-y divide-slate-100 overflow-y-auto rounded-lg ring-1 ring-slate-200 dark:divide-slate-800 dark:ring-slate-800">
          {teams.isLoading && <li className="flex justify-center p-6 text-brand-600"><Spinner /></li>}
          {teams.data?.teams.length === 0 && <li className="p-6 text-center text-sm text-slate-500">No teams yet.</li>}
          {teams.data?.teams.map((t) => (
            <li key={t.id} className="flex items-center gap-3 px-4 py-3">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand-50 text-sm font-semibold text-brand-700 dark:bg-brand-700/30 dark:text-brand-100">{t.name.slice(0, 2).toUpperCase()}</span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5 font-medium">{t.name}{t.visibility === "private" && <Icon name="lock" className="h-3.5 w-3.5 text-slate-400" />}</span>
                <span className="block truncate text-sm text-slate-500">{t.description || `${t.memberCount} ${t.memberCount === 1 ? "member" : "members"}`}</span>
              </span>
              {t.myRole
                ? <Button size="sm" variant="secondary" onClick={() => { onClose(); onManage(t.id); }}>Open</Button>
                : t.visibility === "public" && atLeast(role, "member") ? <Button size="sm" loading={busy === t.id} onClick={() => join(t)}>Join</Button> : <Button size="sm" variant="ghost" onClick={() => { onClose(); onManage(t.id); }}>View</Button>}
            </li>
          ))}
        </ul>
        <div className="flex justify-between gap-2">
          {atLeast(role, "member") ? <Button variant="secondary" onClick={() => { onClose(); onCreate(); }}><Icon name="plus" className="h-4 w-4" />Create a team</Button> : <span />}
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </div>
      </div>
    </Dialog>
  );
}

// A team's channels, people and settings.
export function TeamDialog({ teamId, onClose }: { teamId: string | null; onClose: () => void }) {
  const { org, me } = useOrg();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState<"channels" | "people" | "settings">("channels");
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState<Member[]>([]);
  const team = useQuery({ queryKey: keys.team(org.slug, teamId ?? ""), queryFn: () => api<{ team: Team }>(`/orgs/${org.slug}/teams/${teamId}`), enabled: Boolean(teamId) });
  const t = team.data?.team;
  const refresh = () => Promise.all([
    qc.invalidateQueries({ queryKey: keys.team(org.slug, teamId ?? "") }),
    qc.invalidateQueries({ queryKey: keys.teams(org.slug) }),
    qc.invalidateQueries({ queryKey: keys.conversations(org.slug) }),
  ]);
  const run = async (fn: () => Promise<unknown>) => { setError(null); try { await fn(); await refresh(); } catch (err) { setError(errorText(err)); } };
  const close = () => { setTab("channels"); setError(null); setAdding([]); onClose(); };
  const base = `/orgs/${org.slug}/teams/${teamId}`;

  async function createChannel(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    await run(async () => {
      const r = await api<{ conversation: Conversation }>(`${base}/channels`, { method: "POST", body: { name: data.get("name"), isPrivate: data.get("private") === "on" } });
      form.reset();
      close();
      navigate(`/o/${org.slug}/chat/${r.conversation.id}`);
    });
  }
  async function saveSettings(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    await run(() => api(base, { method: "PATCH", body: { name: data.get("name"), description: data.get("description"), visibility: data.get("visibility") } }));
  }

  return (
    <Dialog open={Boolean(teamId)} onClose={close} title={t?.name ?? "Team"}>
      {!t ? <div className="flex justify-center p-6 text-brand-600">{team.error ? <Alert>{errorText(team.error)}</Alert> : <Spinner />}</div> : (
        <div className="space-y-4">
          {t.description && <p className="-mt-2 text-sm text-slate-600 dark:text-slate-400">{t.description}</p>}
          <div role="tablist" className="flex gap-1 border-b border-slate-200 dark:border-slate-800">
            {(["channels", "people", ...(t.canManage ? ["settings"] as const : [])] as const).map((name) => (
              <button key={name} role="tab" type="button" aria-selected={tab === name} onClick={() => setTab(name)}
                className={cx("-mb-px border-b-2 px-3 py-2 text-sm font-medium capitalize", tab === name ? "border-brand-600 text-brand-700 dark:text-brand-100" : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200")}>
                {name === "people" ? `People (${t.members.length})` : name}
              </button>
            ))}
          </div>
          {error && <Alert>{error}</Alert>}

          {tab === "channels" && (
            <div className="space-y-4">
              <ul className="divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200 dark:divide-slate-800 dark:ring-slate-800">
                {t.channels.map((c) => (
                  <li key={c.id} className="flex items-center gap-3 px-4 py-2.5">
                    <Icon name={c.isPrivate ? "lock" : "hash"} className="h-4 w-4 text-slate-400" />
                    <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium">{c.name}</span>{c.topic && <span className="block truncate text-xs text-slate-500">{c.topic}</span>}</span>
                    {c.joined
                      ? <Button size="sm" variant="secondary" onClick={() => { close(); navigate(`/o/${org.slug}/chat/${c.id}`); }}>Open</Button>
                      : t.myRole && <Button size="sm" onClick={() => run(async () => { await api(`/orgs/${org.slug}/channels/${c.id}/join`, { method: "POST" }); close(); navigate(`/o/${org.slug}/chat/${c.id}`); })}>Join</Button>}
                    {t.canManage && !c.isGeneral && (
                      <button type="button" aria-label={`Delete ${c.name}`} onClick={() => { if (confirm(`Delete the channel "${c.name}"? Its messages will no longer be available.`)) void run(() => api(`/orgs/${org.slug}/channels/${c.id}`, { method: "DELETE" })); }}
                        className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40"><Icon name="trash" className="h-4 w-4" /></button>
                    )}
                  </li>
                ))}
              </ul>
              {t.myRole && (
                <form onSubmit={createChannel} className="space-y-3 rounded-lg bg-slate-50 p-4 dark:bg-slate-800/50">
                  <h3 className="text-sm font-semibold">New channel</h3>
                  <div className="flex gap-2">
                    <input name="name" required minLength={2} maxLength={50} placeholder="e.g. announcements" aria-label="Channel name" className="block min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
                    <Button type="submit" size="sm">Create</Button>
                  </div>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="private" className="h-4 w-4 rounded" />Private: only people I add</label>
                </form>
              )}
            </div>
          )}

          {tab === "people" && (
            <div className="space-y-4">
              <ul className="max-h-72 divide-y divide-slate-100 overflow-y-auto rounded-lg ring-1 ring-slate-200 dark:divide-slate-800 dark:ring-slate-800">
                {t.members.map((m) => (
                  <li key={m.id} className="flex items-center gap-3 px-4 py-2.5">
                    <Avatar name={m.name} userId={m.id} size="sm" />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">{m.name}{m.id === me.user.id && <span className="font-normal text-slate-500"> (you)</span>}</span>
                    {t.canManage && m.id !== me.user.id ? (
                      <>
                        <select aria-label={`Role for ${m.name}`} value={m.role} onChange={(e) => run(() => api(`${base}/members/${m.id}`, { method: "PATCH", body: { role: e.target.value } }))}
                          className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900">
                          <option value="owner">Owner</option><option value="member">Member</option>
                        </select>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => { if (confirm(`Remove ${m.name} from ${t.name}?`)) void run(() => api(`${base}/members/${m.id}`, { method: "DELETE" })); }}>Remove</Button>
                      </>
                    ) : <Badge tone={m.role === "owner" ? "brand" : "slate"}>{m.role}</Badge>}
                  </li>
                ))}
              </ul>
              {t.canManage && (
                <div className="space-y-3 rounded-lg bg-slate-50 p-4 dark:bg-slate-800/50">
                  <h3 className="text-sm font-semibold">Add people</h3>
                  <PeoplePicker selected={adding} onChange={setAdding} exclude={t.members.map((m) => m.id)} />
                  <div className="flex justify-end"><Button size="sm" disabled={!adding.length} onClick={() => run(async () => { await api(`${base}/members`, { method: "POST", body: { userIds: adding.map((a) => a.id) } }); setAdding([]); })}>Add {adding.length || ""}</Button></div>
                </div>
              )}
              {t.myRole && (
                <Button variant="secondary" className="text-red-600" onClick={() => { if (confirm(`Leave ${t.name}? You'll leave its channels too.`)) void run(async () => { await api(`${base}/members/me`, { method: "DELETE" }); close(); navigate(`/o/${org.slug}/chat`); }); }}>Leave team</Button>
              )}
            </div>
          )}

          {tab === "settings" && t.canManage && (
            <div className="space-y-6">
              <form onSubmit={saveSettings} className="space-y-4">
                <Field label="Team name" name="name" defaultValue={t.name} required minLength={2} maxLength={60} />
                <Field label="Description" name="description" defaultValue={t.description} maxLength={300} />
                <Select label="Who can find and join it" name="visibility" defaultValue={t.visibility}>
                  <option value="public">Public: anyone in {org.name} can join</option>
                  <option value="private">Private: only people the owners add</option>
                </Select>
                <div className="flex justify-end"><Button type="submit">Save</Button></div>
              </form>
              <div className="rounded-lg p-4 ring-1 ring-red-200 dark:ring-red-900">
                <h3 className="font-semibold">Delete this team</h3>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Its channels and their messages will no longer be available to anyone.</p>
                <Button variant="danger" size="sm" className="mt-3" onClick={() => { if (confirm(`Delete ${t.name} for everyone?`)) void run(async () => { await api(base, { method: "DELETE" }); close(); navigate(`/o/${org.slug}/chat`); }); }}>Delete team</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}

// Add people to a group chat or channel.
export function AddPeopleDialog({ conversation, open, onClose }: { conversation: Conversation; open: boolean; onClose: () => void }) {
  const { org } = useOrg();
  const qc = useQueryClient();
  const [people, setPeople] = useState<Member[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Channels: only people already in the team.
  const team = useQuery({ queryKey: keys.team(org.slug, conversation.team?.id ?? ""), queryFn: () => api<{ team: Team }>(`/orgs/${org.slug}/teams/${conversation.team!.id}`), enabled: open && Boolean(conversation.team) });
  const limitTo = conversation.team ? team.data?.team.members.map((m) => m.id) ?? [] : undefined;
  const close = () => { setPeople([]); setError(null); onClose(); };
  async function add(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api(`/orgs/${org.slug}/conversations/${conversation.id}/members`, { method: "POST", body: { userIds: people.map((p) => p.id) } });
      await qc.invalidateQueries({ queryKey: keys.conversation(org.slug, conversation.id) });
      close();
    } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onClose={close} title={`Add people to ${conversation.title}`}>
      <form onSubmit={add} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        {conversation.team && <p className="text-sm text-slate-500">People need to be in {conversation.team.name} to join its channels.</p>}
        <PeoplePicker selected={people} onChange={setPeople} exclude={conversation.members.map((m) => m.id)} limitTo={limitTo} />
        <div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={close}>Cancel</Button><Button type="submit" loading={busy} disabled={!people.length}>Add</Button></div>
      </form>
    </Dialog>
  );
}
